import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../api';
import { unwrapData } from '@wapharma/shared';;
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { openMessagesFlyout } from '../lib/messagesFlyout';
import { useToast } from '../contexts/ToastContext';
import { deferAfterPaint } from '../lib/deferAfterPaint';
import { isCatalogImportInboxType } from '../components/inboxCatalogImport';
import { dispatchCatalogImportSeen } from '../utils/catalogImportSeenStorage';

export type InboxItem = {
  id: string;
  type: string;
  entityType: string;
  entityId: string;
  severity: 'low' | 'medium' | 'high';
  whyNow: string;
  nextAction: string;
  ownerRole: string;
  seen: boolean;
  seenAt: string | null;
};

type InboxListResponse = {
  data: InboxItem[];
  meta?: { unseen?: number; total?: number };
};

export function inboxItemKey(i: Pick<InboxItem, 'ownerRole' | 'type' | 'entityId'>) {
  return `${i.ownerRole}|${i.type}|${i.entityId}`;
}

export function useInboxListState() {
  const nav = useNavigate();
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const [items, setItems] = useState<InboxItem[]>([]);
  const [pending, setPending] = useState<string | null>(null);
  const loadAbortRef = useRef<AbortController | null>(null);

  const reportError = useCallback((e: unknown) => {
    showToastRef.current(getDisplayError(e), { tone: 'error' });
  }, []);

  const load = useCallback(() => {
    const token = getToken();
    if (!token) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    void apiGet<InboxListResponse>(`/inbox`, token, controller.signal)
      .then((b) => {
        if (controller.signal.aborted) return;
        const list = unwrapData<InboxItem[]>(b);
        setItems(list);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        reportError(e);
      });
  }, [reportError]);

  useEffect(() => {
    const cancelDefer = deferAfterPaint(() => load());
    return () => {
      cancelDefer();
      loadAbortRef.current?.abort();
      loadAbortRef.current = null;
    };
  }, [load]);

  useEffect(() => {
    const onReload = () => load();
    window.addEventListener('wapharma-inbox-reload', onReload);
    return () => window.removeEventListener('wapharma-inbox-reload', onReload);
  }, [load]);

  const markSeen = useCallback(
    async (i: Pick<InboxItem, 'ownerRole' | 'type' | 'entityId'>) => {
      const token = getToken();
      if (!token) return;
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((row) =>
          row.ownerRole === i.ownerRole &&
          row.type === i.type &&
          row.entityId === i.entityId
            ? { ...row, seen: true, seenAt: now }
            : row,
        ),
      );
      if (i.ownerRole === 'catalog' && isCatalogImportInboxType(i.type)) {
        dispatchCatalogImportSeen(i.entityId);
      }
      try {
        await apiPost(
          `/inbox/seen`,
          {
            items: [
              {
                ownerRole: i.ownerRole,
                type: i.type,
                entityId: i.entityId,
              },
            ],
          },
          token,
        );
      } catch (e) {
        reportError(e);
        load();
      }
    },
    [load, reportError],
  );

  const snooze24h = async (i: InboxItem) => {
    const token = getToken();
    if (!token) return;
    setPending(i.id);
    const snoozedUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      await apiPost(
        `/inbox/snooze`,
        { type: i.type, entityId: i.entityId, ownerRole: i.ownerRole, entityType: i.entityType, snoozedUntil },
        token,
      );
      await load();
    } catch (e: unknown) {
      reportError(e);
    } finally {
      setPending(null);
    }
  };

  const dismiss = async (i: InboxItem) => {
    const token = getToken();
    if (!token) return;
    setPending(i.id);
    try {
      await apiPost(
        `/inbox/dismiss`,
        { type: i.type, entityId: i.entityId, ownerRole: i.ownerRole, entityType: i.entityType },
        token,
      );
      await load();
    } catch (e: unknown) {
      reportError(e);
    } finally {
      setPending(null);
    }
  };

  const openEntity = (i: InboxItem) => {
    if (
      i.type === 'SUPPORT_TICKET_OPEN' ||
      i.type === 'SUPPORT_TICKET_REPLY' ||
      i.type === 'SUPPORT_TICKET_URGENT'
    ) {
      nav(`/support/${encodeURIComponent(i.entityId)}`);
      return;
    }
    if (i.type === 'CONVERSATION_REPLY') {
      if (i.entityType === 'Conversation') {
        openMessagesFlyout({ conversationId: i.entityId });
        return;
      }
      return;
    }
    if (i.type === 'PO_ORDERED') {
      return;
    }
    if (i.type === 'ORDER_PENDING') {
      nav(`/orders/${encodeURIComponent(i.entityId)}`);
      return;
    }
    if (i.type === 'BATCH_NEAR_EXPIRY') {
      nav(`/inventory/lots/${encodeURIComponent(i.entityId)}`);
      return;
    }
    if (i.type === 'GTIN_SOURCE_UPDATE') {
      nav('/settings/data-sources');
      return;
    }
    if (
      i.type === 'CATALOG_IMPORT_ACTIVE' ||
      i.type === 'CATALOG_IMPORT_COMPLETED' ||
      i.type === 'CATALOG_IMPORT_FAILED' ||
      i.type === 'CATALOG_IMPORT_CANCELLED'
    ) {
      nav('/catalog');
      return;
    }
    nav('/catalog');
  };

  const isJobSeen = useCallback(
    (jobId: string, type: string) =>
      items.some(
        (row) => row.entityId === jobId && row.type === type && row.ownerRole === 'catalog' && row.seen,
      ),
    [items],
  );

  return {
    items,
    pending,
    reload: load,
    snooze24h,
    dismiss,
    openEntity,
    markSeen,
    isJobSeen,
  };
}
